import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import LandingPage from './pages/LandingPage';
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage, EmailVerificationPage } from './pages/AuthPages';
import PublicFighterPage from './pages/PublicFighterPage';
import DashboardPage from './pages/DashboardPage';
import { FightersListPage, FighterFormPage, FighterDetailPage } from './pages/FightersPages';
import { TrainingCampsListPage, TrainingCampFormPage } from './pages/TrainingCampsPages';
import { AssessmentsListPage, AssessmentFormPage } from './pages/AssessmentsPages';
import { FightsListPage, FightFormPage } from './pages/FightsPages';
import { SubscriptionPage, SubscriptionSuccessPage } from './pages/SubscriptionPage';
import SettingsPage from './pages/SettingsPage';
import VideosPage from './pages/VideosPage';
import { AdminPanelPage, AdminSetupPage } from './pages/AdminPages';
import WeightTrackingPage from './pages/WeightTrackingPage';
import AttendancePage from './pages/AttendancePage';
import CampCalendarPage from './pages/CampCalendarPage';
import FighterComparisonPage from './pages/FighterComparisonPage';
import ReportsPage from './pages/ReportsPage';
import NotificationsPage from './pages/NotificationsPage';
import { TermsPage, PrivacyPage } from './pages/LegalPages';
import HelpPage from './pages/HelpPage';
import ReferralPage from './pages/ReferralPage';
import FightCountdownPage from './pages/FightCountdownPage';
import SocialSharingPage from './pages/SocialSharingPage';
import MembershipPlansPage from './pages/MembershipPlansPage';
import MembershipSuccessPage from './pages/MembershipSuccessPage';

// Fighter Portal Pages
import FighterDashboard from './pages/FighterDashboard';
import FighterRegisterPage from './pages/FighterRegisterPage';
import FighterLoginPage from './pages/FighterLoginPage';
import FighterProfilePage from './pages/FighterProfilePage';
import FighterNotesPage from './pages/FighterNotesPage';
import FightAnalysisPage from './pages/FightAnalysisPage';

import TrialExpiredModal from './components/TrialExpiredModal';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { token, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (token) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/fighter/:shareToken" element={<PublicFighterPage />} />
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPasswordPage />
        </PublicRoute>
      } />
      <Route path="/reset-password" element={
        <PublicRoute>
          <ResetPasswordPage />
        </PublicRoute>
      } />
      <Route path="/verify-email" element={<EmailVerificationPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      
      {/* Fighter Portal */}
      <Route path="/fighter/register" element={<FighterRegisterPage />} />
      <Route path="/fighter/login" element={<FighterLoginPage />} />
      <Route path="/fighter/dashboard" element={
        <ProtectedRoute>
          <FighterDashboard />
        </ProtectedRoute>
      } />
      <Route path="/fighter/profile" element={
        <ProtectedRoute>
          <FighterProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/fighter/notes" element={
        <ProtectedRoute>
          <FighterNotesPage />
        </ProtectedRoute>
      } />
      <Route path="/fighter/analysis" element={
        <ProtectedRoute>
          <FightAnalysisPage />
        </ProtectedRoute>
      } />
      <Route path="/fighter/analysis/:fightId" element={
        <ProtectedRoute>
          <FightAnalysisPage />
        </ProtectedRoute>
      } />
      
      {/* Fighters */}
      <Route path="/fighters" element={
        <ProtectedRoute>
          <FightersListPage />
        </ProtectedRoute>
      } />
      <Route path="/fighters/new" element={
        <ProtectedRoute>
          <FighterFormPage />
        </ProtectedRoute>
      } />
      <Route path="/fighters/:id" element={
        <ProtectedRoute>
          <FighterDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/fighters/:id/edit" element={
        <ProtectedRoute>
          <FighterFormPage />
        </ProtectedRoute>
      } />
      
      {/* Training Camps */}
      <Route path="/training-camps" element={
        <ProtectedRoute>
          <TrainingCampsListPage />
        </ProtectedRoute>
      } />
      <Route path="/training-camps/new" element={
        <ProtectedRoute>
          <TrainingCampFormPage />
        </ProtectedRoute>
      } />
      <Route path="/training-camps/:id/edit" element={
        <ProtectedRoute>
          <TrainingCampFormPage />
        </ProtectedRoute>
      } />
      
      {/* Assessments */}
      <Route path="/assessments" element={
        <ProtectedRoute>
          <AssessmentsListPage />
        </ProtectedRoute>
      } />
      <Route path="/assessments/new" element={
        <ProtectedRoute>
          <AssessmentFormPage />
        </ProtectedRoute>
      } />
      <Route path="/assessments/edit/:id" element={
        <ProtectedRoute>
          <AssessmentFormPage isEdit={true} />
        </ProtectedRoute>
      } />
      
      {/* Fights */}
      <Route path="/fights" element={
        <ProtectedRoute>
          <FightsListPage />
        </ProtectedRoute>
      } />
      <Route path="/fights/new" element={
        <ProtectedRoute>
          <FightFormPage />
        </ProtectedRoute>
      } />
      <Route path="/fights/:id/edit" element={
        <ProtectedRoute>
          <FightFormPage />
        </ProtectedRoute>
      } />
      
      {/* Videos */}
      <Route path="/videos" element={
        <ProtectedRoute>
          <VideosPage />
        </ProtectedRoute>
      } />
      
      {/* Weight Tracking */}
      <Route path="/weight-tracking" element={
        <ProtectedRoute>
          <WeightTrackingPage />
        </ProtectedRoute>
      } />
      
      {/* Attendance */}
      <Route path="/attendance" element={
        <ProtectedRoute>
          <AttendancePage />
        </ProtectedRoute>
      } />
      
      {/* Camp Calendar */}
      <Route path="/calendar" element={
        <ProtectedRoute>
          <CampCalendarPage />
        </ProtectedRoute>
      } />
      
      {/* Subscription */}
      <Route path="/subscription" element={
        <ProtectedRoute>
          <SubscriptionPage />
        </ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute>
          <SubscriptionSuccessPage />
        </ProtectedRoute>
      } />
      
      {/* Membership Plans */}
      <Route path="/membership-plans" element={
        <ProtectedRoute>
          <MembershipPlansPage />
        </ProtectedRoute>
      } />
      <Route path="/membership/success" element={
        <ProtectedRoute>
          <MembershipSuccessPage />
        </ProtectedRoute>
      } />
      
      {/* Settings */}
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      {/* Fighter Comparison */}
      <Route path="/compare" element={
        <ProtectedRoute>
          <FighterComparisonPage />
        </ProtectedRoute>
      } />
      
      {/* Reports */}
      <Route path="/reports" element={
        <ProtectedRoute>
          <ReportsPage />
        </ProtectedRoute>
      } />
      
      {/* Notifications */}
      <Route path="/notifications" element={
        <ProtectedRoute>
          <NotificationsPage />
        </ProtectedRoute>
      } />
      
      {/* Help Center */}
      <Route path="/help" element={
        <ProtectedRoute>
          <HelpPage />
        </ProtectedRoute>
      } />
      
      {/* Referral Program */}
      <Route path="/referrals" element={
        <ProtectedRoute>
          <ReferralPage />
        </ProtectedRoute>
      } />
      
      {/* Fight Countdown */}
      <Route path="/countdown" element={
        <ProtectedRoute>
          <FightCountdownPage />
        </ProtectedRoute>
      } />
      
      {/* Social Sharing */}
      <Route path="/social" element={
        <ProtectedRoute>
          <SocialSharingPage />
        </ProtectedRoute>
      } />
      
      {/* Super Admin */}
      <Route path="/admin/setup" element={<AdminSetupPage />} />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPanelPage />
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <TrialExpiredModal />
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: '#18181b',
              border: '1px solid #27272a',
              color: '#fafafa',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
